import React, { useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
  ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import * as XLSX from "xlsx";
import { consultaMasivaChips } from "../services/centroEquiposService";
import BusquedaPorIdentificador from "./BusquedaPorIdentificador";

const MAX = 3000;
const cellSx = { py: 0.25, px: 0.75, fontSize: 12, whiteSpace: "nowrap" };

// Columnas de la tabla (y del Excel que se copia o descarga)
const COLUMNAS = [
  ["Consultado", (i) => i.consulta],
  ["Chip", (i) => i.numero || ""],
  ["ICCID", (i) => i.emai || ""],
  ["Modelo del chip", (i) => i.chip_modelo || ""],
  ["Estado del chip", (i) => i.chip_estado || ""],
  ["Equipo", (i) => (i.equipo_id ? `${i.equipo_tipo}: ${i.equipo_id}` : i.encontrado ? "Sin módem" : "")],
  ["Modelo del equipo", (i) => i.equipo_modelo || ""],
  ["Estado del equipo", (i) => i.equipo_estado || ""],
  ["Placa", (i) => i.placa || ""],
  ["Cliente", (i) => i.cliente || ""],
];

// Del texto pegado (una columna de Excel, o varias) saca los números: separa por saltos de línea, tabulaciones, espacios, comas o punto y coma,
// y descarta lo que no lleva ningún dígito (encabezados como "Chip" o "Número").
const extraerNumeros = (texto) => [...new Set(String(texto || "").split(/[\s,;]+/).map((t) => t.trim().replace(/^"|"$/g, "")).filter((t) => t.length >= 4 && /\d/.test(t)))];

// Consulta de varios chips a la vez: se pegan los números desde Excel y se ve en qué módem, placa y cliente está cada uno.
export default function ConsultaMasivaChips() {
  const [texto, setTexto] = useState("");
  const [resultado, setResultado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [vista, setVista] = useState("todos");
  const [copiado, setCopiado] = useState(false);
  const [chipDialog, setChipDialog] = useState(null); // número del chip cuyo detalle se está viendo

  const numeros = useMemo(() => extraerNumeros(texto), [texto]);

  const consultar = async () => {
    setCargando(true);
    setError("");
    setResultado(null);
    try {
      const { data } = await consultaMasivaChips(numeros);
      setResultado(data?.data ?? data);
      setVista("todos");
    } catch (e) {
      setError(e?.response?.data?.message || "No se pudo hacer la consulta.");
    } finally {
      setCargando(false);
    }
  };

  const items = resultado?.items || [];
  const noEncontrados = items.filter((i) => !i.encontrado).length;
  const visibles = vista === "faltan" ? items.filter((i) => !i.encontrado) : items;

  const filasExcel = () => visibles.map((i) => Object.fromEntries(COLUMNAS.map(([h, f]) => [h, f(i)])));
  const copiar = async () => {
    const tsv = [COLUMNAS.map(([h]) => h).join("\t"), ...visibles.map((i) => COLUMNAS.map(([, f]) => String(f(i)).replace(/[\t\r\n]+/g, " ")).join("\t"))].join("\n");
    try { await navigator.clipboard.writeText(tsv); setCopiado(true); setTimeout(() => setCopiado(false), 2500); } catch { setError("No se pudo copiar. Usa «Descargar Excel»."); }
  };
  const descargar = () => {
    const ws = XLSX.utils.json_to_sheet(filasExcel());
    ws["!cols"] = COLUMNAS.map(([h]) => ({ wch: Math.max(h.length + 2, 16) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Chips");
    XLSX.writeFile(wb, `consulta_chips_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 800 }}>Consulta de chips</Typography>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 1 }}>
          Copia la columna de chips en Excel (Ctrl+C) y pégala aquí. Acepta el número del chip o su ICCID. Verás en qué módem, placa y cliente está cada uno.
        </Typography>
        <TextField
          multiline minRows={4} maxRows={10} fullWidth size="small" value={texto} onChange={(e) => { setTexto(e.target.value); setResultado(null); }}
          placeholder={"67703879\n76572075\n69802263"}
          sx={{ "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: "0.8rem" } }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1, flexWrap: "wrap" }}>
          <Button
            variant="contained" size="small" onClick={consultar} disabled={cargando || !numeros.length || numeros.length > MAX}
            startIcon={cargando ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />} sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Consultar
          </Button>
          <Typography sx={{ fontSize: 12, color: numeros.length > MAX ? "error.main" : "text.secondary" }}>
            {numeros.length > MAX ? `Son ${numeros.length} números; el máximo es ${MAX}.` : numeros.length ? `${numeros.length} número${numeros.length === 1 ? "" : "s"} para consultar` : "Aún no hay números pegados"}
          </Typography>
          {texto && <Button size="small" color="inherit" onClick={() => { setTexto(""); setResultado(null); setError(""); }} sx={{ textTransform: "none" }}>Limpiar</Button>}
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 1, py: 0 }} onClose={() => setError("")}>{error}</Alert>}

      {resultado && (
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <Box sx={{ p: 1.25, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", bgcolor: (t) => alpha(t.palette.text.primary, 0.03) }}>
            <Chip size="small" color="success" variant="outlined" label={`${resultado.encontrados} encontrado${resultado.encontrados === 1 ? "" : "s"}`} sx={{ fontWeight: 700 }} />
            <Chip size="small" color={noEncontrados ? "warning" : "default"} variant="outlined" label={`${noEncontrados} no encontrado${noEncontrados === 1 ? "" : "s"}`} sx={{ fontWeight: 700 }} />
            <ToggleButtonGroup size="small" exclusive value={vista} onChange={(_, v) => v && setVista(v)} sx={{ ml: 1, "& .MuiToggleButton-root": { py: 0.2, px: 1.25, fontSize: 12, textTransform: "none" } }}>
              <ToggleButton value="todos">Todos</ToggleButton>
              <ToggleButton value="faltan" disabled={!noEncontrados}>Solo no encontrados</ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title="Copia la tabla para pegarla en Excel">
              <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={copiar} sx={{ textTransform: "none" }}>{copiado ? "¡Copiado!" : "Copiar"}</Button>
            </Tooltip>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={descargar} sx={{ textTransform: "none" }}>Descargar Excel</Button>
          </Box>
          <TableContainer sx={{ maxHeight: "62vh" }}>
            <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": cellSx }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>#</TableCell>
                  {COLUMNAS.map(([h]) => <TableCell key={h} sx={{ fontWeight: 800, bgcolor: "background.paper" }}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibles.map((i, n) => (
                  <TableRow key={i.consulta} hover sx={i.encontrado ? undefined : { bgcolor: (t) => alpha(t.palette.warning.main, 0.1) }}>
                    <TableCell sx={{ color: "text.disabled" }}>{n + 1}</TableCell>
                    {COLUMNAS.map(([h, f]) => {
                      const v = f(i);
                      if (h === "Consultado") return <TableCell key={h} sx={{ fontFamily: "monospace", fontWeight: 700 }}>{v}</TableCell>;
                      if (!i.encontrado && h === "Chip") return <TableCell key={h}><Chip size="small" color="warning" label="No encontrado" sx={{ height: 18, fontSize: 10.5 }} /></TableCell>;
                      if (h === "Equipo" && v === "Sin módem") return <TableCell key={h} sx={{ color: "text.secondary", fontStyle: "italic" }}>Sin módem</TableCell>;
                      if (h === "Chip" && v) {
                        return (
                          <TableCell key={h}>
                            <Tooltip title="Ver información del chip">
                              <Box component="a" href="#chip" onClick={(e) => { e.preventDefault(); setChipDialog(i.numero); }}
                                sx={{ color: "primary.main", cursor: "pointer", fontFamily: "monospace", fontWeight: 700, textDecoration: "underline dotted", "&:hover": { textDecoration: "underline" } }}>
                                {v}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      }
                      if (h === "Placa" && v) return <TableCell key={h} sx={{ fontWeight: 700 }}>{v}</TableCell>;
                      return <TableCell key={h}>{v}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={Boolean(chipDialog)} onClose={() => setChipDialog(null)} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ py: 0.75, px: 1.25, fontSize: 14, fontWeight: 800 }}>
          Chip {chipDialog}
          <IconButton size="small" onClick={() => setChipDialog(null)} sx={{ position: "absolute", right: 8, top: 5 }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0.75 }}>
          {chipDialog && <BusquedaPorIdentificador initialTipo="chip" initialBuscar={chipDialog} autoLoadKey={`chip-${chipDialog}`} hideSearch />}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
